import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Input, Select } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { AdminTable, AdminToolbar, Td } from './AdminTable.jsx'

/* Admin — global VAT-rate CRUD (super-admin). POST/PUT/DELETE /admin/vat-rates.
 * `code` is immutable after creation (inventory §3.7). */
const CATEGORIES = ['S', 'Z', 'E', 'AE', 'K', 'G', 'O']

export default function VatRatesTab() {
  const { t } = useTranslation()
  const { confirm, toast } = useFeedback()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/vat-rates/').then(({ data }) => setRows(data?.vat_rates || [])).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const remove = async (r) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('admin.vat.confirmDelete'), message: r.name, confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/admin/vat-rates/${r.id}`); toast(t('admin.vat.deleted'), 'success'); load() }
    catch (e) { toast(e?.response?.data?.error || t('admin.errorGeneric'), 'error') }
  }

  const columns = [
    { key: 'code', label: t('admin.vat.code') }, { key: 'name', label: t('admin.vat.name') },
    { key: 'rate', label: t('admin.vat.rate'), align: 'right' }, { key: 'cat', label: t('admin.vat.category') },
    { key: 'status', label: t('admin.companies.status') }, { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <AdminToolbar action={<Button variant="primary" icon="plus" onClick={() => setModal({})}>{t('admin.vat.new')}</Button>} />
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.vat.empty')}
        renderRow={(r) => (
          <>
            <Td strong style={{ fontFamily: 'monospace' }}>{r.code}</Td>
            <Td>{r.name}</Td>
            <Td align="right">{r.rate}%</Td>
            <Td>{r.vat_category_code}</Td>
            <Td><Badge tone={r.is_active ? 'success' : 'neutral'}>{r.is_active ? t('admin.active') : t('admin.inactive')}</Badge></Td>
            <Td align="right"><div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
              <IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setModal({ rate: r })} />
              <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => remove(r)} />
            </div></Td>
          </>
        )} />
      {modal && <VatModal t={t} rate={modal.rate} onClose={() => setModal(null)} onSaved={() => { setModal(null); toast(t('admin.vat.saved'), 'success'); load() }} />}
    </>
  )
}

function VatModal({ t, rate, onClose, onSaved }) {
  const isEdit = !!rate
  const [f, setF] = useState(rate ? { ...rate } : { name: '', code: '', rate: 0, vat_category_code: 'S', is_active: true })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true); setError('')
    const payload = { name: f.name, rate: Number(f.rate), vat_category_code: f.vat_category_code, is_active: f.is_active }
    if (!isEdit) payload.code = f.code
    try {
      if (isEdit) await api.put(`/admin/vat-rates/${rate.id}`, payload)
      else await api.post('/admin/vat-rates/', payload)
      onSaved()
    } catch (e2) { setError(e2?.response?.data?.error || t('admin.errorGeneric')) } finally { setSaving(false) }
  }

  return (
    <Modal open onClose={onClose} maxWidth={480} closeOnBackdrop={false}>
      <form onSubmit={submit} style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>{isEdit ? t('admin.vat.edit') : t('admin.vat.new')}</h3>
        {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}
        <Field label={t('admin.vat.name')} required><Input value={f.name} onChange={(e) => set('name', e.target.value)} required /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label={t('admin.vat.code')} required hint={isEdit ? t('admin.vat.codeImmutable') : undefined}><Input value={f.code} onChange={(e) => set('code', e.target.value)} disabled={isEdit} required /></Field>
          <Field label={t('admin.vat.rate')} required><Input type="number" step="0.01" min="0" value={f.rate} onChange={(e) => set('rate', e.target.value)} required /></Field>
        </div>
        <Field label={t('admin.vat.category')} required>
          <Select value={f.vat_category_code} onChange={(e) => set('vat_category_code', e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select>
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, marginBottom: 16 }}>
          <input type="checkbox" checked={f.is_active} onChange={(e) => set('is_active', e.target.checked)} /> {t('admin.active')}
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon="check" loading={saving}>{t('common.save')}</Button>
        </div>
      </form>
    </Modal>
  )
}
